/**
 * Type-safe route path constants.
 *
 * All navigable paths live here so that changes to route structure
 * surface compile-time errors in consumers (CommandPalette, Sidebar, etc.)
 * instead of silently breaking at runtime.
 */

/**
 * Selection kinds addressable via `paths.pageComposerSelection`. Mirrors
 * `ComposerSelection["type"]` from `~/features/pageComposer` without importing
 * the feature into `app/config` — see AGENTS.md "Where code goes".
 */
export type ComposerSelectionType =
  | "menuItem"
  | "tab"
  | "recordPage"
  | "recordTab"
  | "slot"
  | "childPage"
  | "action"
  | "viewPart";

/**
 * Mirrors `NavActionUsage` from `~/features/navigation` without importing the feature into
 * `app/config` — same reasoning as `ComposerSelectionType` above.
 */
export type WorkflowLinkUsage = "CREATE" | "RECORD_ACTION" | "BULK_ACTION" | "ROW_ACTION";

/** The usages a user can pick for a new link — exported so a caller that only has the raw string
 *  off a URL (`routes/workflows/new.tsx`'s `?usage=`) can validate against it before trusting it.
 *  `BULK_ACTION` stays in the type (existing links still parse and show) but is not offered: no
 *  runtime renders a bulk action yet, so a new one would never start. Add it back here once a
 *  table selection toolbar runs `BULK_ACTION` actions. */
export const WORKFLOW_LINK_USAGES: readonly WorkflowLinkUsage[] = [
  "CREATE",
  "RECORD_ACTION",
  "ROW_ACTION",
];

/**
 * Builds the `?sel=` value for `paths.pageComposerSelection` — `hostSlot` is the child-page's own
 * host slot for `childPage` and doubles as the viewPart id for `viewPart` (see that path's doc).
 */
const composerSelParam = (
  type: ComposerSelectionType,
  key: string,
  hostSlot: string | undefined,
): string => {
  if (type === "childPage" && hostSlot) {
    return `childPage:${hostSlot}:${key}`;
  }

  if (type === "viewPart" && hostSlot) {
    return `viewPart:${key}:${hostSlot}`;
  }

  return `${type}:${key}`;
};

/**
 * `?panel=` value asking the composer to open the selection's inspector on its View half — the
 * view editor rather than the element's own fields. Read once as the composer loads, then dropped.
 */
export const COMPOSER_VIEW_PANEL = "view";

/**
 * `?panel=` value asking the composer to open a page's Content container on its View half, where
 * the page's perspectives are edited. Read once as the composer loads, then dropped.
 */
export const COMPOSER_PERSPECTIVES_PANEL = "perspectives";

/** `?newRecordPage=<Schema>@<version>`: the composer opens on the "New record page" question for
 *  that object — the header's entry from a record whose object has no record page yet. */
export const COMPOSER_NEW_RECORD_PAGE_PARAM = "newRecordPage";

/** Starts the `?sel=` value of the composer's single-view page (`singleViewSelParam`). */
export const SINGLE_VIEW_SEL_PREFIX = "view:";

/** The single-view page's `?sel=`: `view:<schema>@<version>/<view>`. A schema name and a version
 *  hold no `@` or `/`; the composer's `parseSingleViewParam` reads it back. */
export const singleViewSelParam = (schemaName: string, version: string, viewName: string) =>
  `${SINGLE_VIEW_SEL_PREFIX}${schemaName}@${version}/${viewName}`;

/** The element `paths.pageComposerEditView` opens: a block on the page that hosts it (and the
 *  record that page previews), or a schema workbench tab. */
type ComposerViewEditorTarget =
  | { pageKey: string; recordId?: string; slotKey: string }
  | { tabKey: string };

export const paths = {
  dashboard: "/",
  businessObjects: "/business-objects",
  /** Schema-detail page; bare path is the Records tab. */
  businessObjectsSchemaDetail: (
    schemaName: string,
    version: string,
    tab: "records" | "views" | "attributes" = "records",
  ) => {
    const base = `/business-objects/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}`;

    return tab === "records" ? base : `${base}/${tab}`;
  },
  /** Alias for the Records tab on the schema-detail page. */
  businessObjectsList: (schemaName: string, version: string) =>
    `/business-objects/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}` as const,
  businessObjectDetail: (schemaName: string, version: string, objectId: string) =>
    `/business-objects/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}/${encodeURIComponent(objectId)}` as const,
  schemaRegistry: "/schema-registry",
  schemaRegistryModel: "/schema-registry/model",
  schemaRegistryGraph: "/schema-registry/graph",
  /**
   * Configuration Workbench Entity Hub — one resource @ version with layer tabs
   * (Schema → Graph → Policy → Reactions → Workflows → Views → Release → Records).
   * Rollup patterns are authored on Reactions (`AddPatternDialog`).
   */
  schemaRegistryEntityHub: (
    schemaName: string,
    version: string,
    layer:
      | "schema"
      | "graph"
      | "policy"
      | "reactions"
      | "workflows"
      | "views"
      | "release"
      | "records" = "schema",
  ) => {
    const base = `/schema-registry/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}`;
    return layer === "schema" ? base : (`${base}/${layer}` as const);
  },
  viewConfiguration: "/configuration/views",
  documents: "/documents",
  /** Manage → Libraries. Material Libraries is the page; Images is the second tab. */
  libraries: "/libraries",
  librariesImages: "/images",
  /** Full-screen picture picker. Must stay a child of `/images` so the catalogue page does not claim it. */
  imagesPick: "/images/pick",
  issues: "/issues",
  settings: "/settings",
  recordLoader: "/admin/record-loader",
  flavourProvisioning: "/admin/flavour-provisioning",
  pageComposer: "/configuration/page-composer",
  /**
   * Composer with a NavPage preselected and an optional preview record / slot. `back` is the live
   * page the link was followed from (its detail page and tab included), where the composer's
   * "Back to page" returns; the composer accepts only a path on this app's own origin.
   */
  pageComposerAt: (pageKey: string, recordId?: string, slotKey?: string, back?: string) => {
    const params = new URLSearchParams();
    params.set("page", pageKey);
    if (recordId) {
      params.set("record", recordId);
    }
    if (slotKey) {
      params.set("slot", slotKey);
    }
    if (back) {
      params.set("back", back);
    }
    return `/configuration/page-composer?${params.toString()}`;
  },
  /**
   * Composer with a specific tree node preselected (`?sel=<type>:<key>`, the `selectionId`
   * format). The third argument is `hostSlot` for a `childPage` selection (`childPage:<hostSlot>:<key>`)
   * and doubles as the viewPart id for a `viewPart` one (`viewPart:<key>:<part>`) — the same
   * two-extra-segments shape, read by whichever field that type actually names.
   *
   * `at`, when given, adds `?at=<type>:<key>` (same `selectionId` format) naming the page/tab the
   * canvas should actually render — for a selection with no page of its own (an `action` lives on
   * its owning page/tab/slot, not a route), so the composer opens on that owner instead of
   * whatever the canvas last showed.
   */
  pageComposerSelection: (
    type: ComposerSelectionType,
    key: string,
    hostSlot?: string,
    at?: { type: ComposerSelectionType; key: string },
  ) => {
    const params = new URLSearchParams();
    params.set("sel", composerSelParam(type, key, hostSlot));
    if (at) {
      params.set("at", composerSelParam(at.type, at.key, undefined));
    }
    return `/configuration/page-composer?${params.toString()}`;
  },
  /**
   * The composer opened on one element's view editor — "Edit view" on a live block or workbench
   * tab: the element selected, its inspector on the View half (`?panel=view`). A block is named on
   * its hosting page the way `pageComposerAt` names it; a schema workbench tab by `?sel=tab:<key>`.
   */
  pageComposerEditView: (target: ComposerViewEditorTarget) => {
    const params = new URLSearchParams();

    if ("tabKey" in target) {
      params.set("sel", composerSelParam("tab", target.tabKey, undefined));
    } else {
      params.set("page", target.pageKey);
      if (target.recordId) {
        params.set("record", target.recordId);
      }
      params.set("slot", target.slotKey);
    }

    params.set("panel", COMPOSER_VIEW_PANEL);
    return `/configuration/page-composer?${params.toString()}`;
  },
  /**
   * The composer opened on a page's perspectives — "Edit perspectives" on a live page's switch:
   * the page shown (with the record it previews), its Content container's View half open
   * (`?panel=perspectives`).
   */
  pageComposerEditPerspectives: (target: { pageKey: string; recordId?: string }) => {
    const params = new URLSearchParams();

    params.set("page", target.pageKey);
    if (target.recordId) {
      params.set("record", target.recordId);
    }
    params.set("panel", COMPOSER_PERSPECTIVES_PANEL);
    return `/configuration/page-composer?${params.toString()}`;
  },
  /**
   * The composer opened on the "New record page" question for an object that has no record page
   * yet — the header's entry from a live record shown on the default page. `record` is that record,
   * which the new page previews; `back` is its URL, where Back returns.
   */
  pageComposerNewRecordPage: (target: {
    schema: string;
    version: string;
    recordId: string;
    back?: string;
  }) => {
    const params = new URLSearchParams();

    params.set(COMPOSER_NEW_RECORD_PAGE_PARAM, `${target.schema}@${target.version}`);
    params.set("record", target.recordId);
    if (target.back) {
      params.set("back", target.back);
    }
    return `/configuration/page-composer?${params.toString()}`;
  },
  /**
   * The composer's single-view page: one view of a business object, edited on its own with no page
   * around it (`?sel=` from `singleViewSelParam`). `back` is the page its Back link returns to,
   * usually the one that linked here; the composer accepts only a path on this app's own origin.
   */
  pageComposerView: (
    schemaName: string,
    version: string,
    viewName: string,
    opts?: { back?: string },
  ) => {
    const params = new URLSearchParams();
    params.set("sel", singleViewSelParam(schemaName, version, viewName));
    if (opts?.back) {
      params.set("back", opts.back);
    }
    return `/configuration/page-composer?${params.toString()}`;
  },
  workflowNew: "/workflows/new",
  /**
   * `/workflows/new` seeded with the resourceLink a composer "New workflow…" round-trip creates it
   * for, and the `back` URL the create page returns to on Cancel / after a successful save.
   * `version` is the business object's own schema version — every schema has its own, so the
   * resourceLink this mints must carry it rather than assume one.
   */
  workflowNewFor: (opts: {
    resource: string;
    version: string;
    usage: WorkflowLinkUsage;
    back: string;
  }) => {
    const params = new URLSearchParams();
    params.set("resource", opts.resource);
    params.set("version", opts.version);
    params.set("usage", opts.usage);
    params.set("back", opts.back);
    return `/workflows/new?${params.toString()}`;
  },
  workflowRunDetail: (runId: string) => `/workflows/runs/${encodeURIComponent(runId)}` as const,
  /** The WorkflowDefinition collection — what `resourceList("WorkflowDefinition", "1.0.0")` builds. */
  workflowList: () => "/WorkflowDefinition/1.0.0" as const,
  /**
   * `opts.back` carries the URL a "Back" affordance on the workflow page returns to (the composer
   * URL that opened it). `opts.version` pins a version other than the highest PUBLISHED one the
   * page shows by default. `opts.mode` opens the editing surface. All absent, the plain path.
   */
  workflowDefinition: (
    definitionKey: string,
    opts?: { back?: string; version?: string; mode?: "view" | "edit" },
  ) => {
    const base = `/workflows/${encodeURIComponent(definitionKey)}`;
    const params = new URLSearchParams();
    if (opts?.back) {
      params.set("back", opts.back);
    }
    if (opts?.version) {
      params.set("version", opts.version);
    }
    if (opts?.mode) {
      params.set("mode", opts.mode);
    }
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  },
  /**
   * Canonical schema-driven resource URL. Mirrors the BE record-service path
   * shape (`/record-service/v1/{resource}/{version}/[{pk}]`) so the URL, the
   * BE endpoint, the schema name, and the ABAC role suffix all use the same
   * identifier. Replaces the per-domain paths (e.g. `/materials`) that used
   * to live here.
   */
  resourceList: (schemaName: string, version: string) =>
    `/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}` as const,
  resourceDetail: (schemaName: string, version: string, objectId: string) =>
    `/${encodeURIComponent(schemaName)}/${encodeURIComponent(version)}/${encodeURIComponent(objectId)}` as const,
  /**
   * Colours & Palettes workbench. Bare path is the Palettes tab.
   *
   * Deliberately BARE — no `?view=`. Landing format / named view live on the
   * NavMenuItem (`preferredFormat` from schema, `view` honoured on WorkspacePage).
   */
  coloursPalettes: (tab = "palettes") =>
    tab === "palettes" ? "/colours-palettes" : (`/colours-palettes/${tab}` as const),
  /** Materials workbench. Bare path is the first tab by nav order. */
  materialsWorkspace: (tab = "materials") =>
    tab === "materials" ? "/materials" : (`/materials/${tab}` as const),
  /** Partners workbench. Bare path is the first tab by nav order. */
  partnersWorkspace: (tab = "overview") =>
    tab === "overview" ? "/partners" : (`/partners/${tab}` as const),
  /** Requests workbench — prototype Supplier Request host UX. */
  requests: "/requests",
  requestDetail: (requestId: string, tab: "asks" | "conversation" | "spec" = "asks") =>
    tab === "asks"
      ? (`/requests/${encodeURIComponent(requestId)}` as const)
      : (`/requests/${encodeURIComponent(requestId)}/${tab}` as const),
  /** Annotate Colours — full-screen, outside the app shell (the artwork wants the viewport). */
  printAnnotator: (printDesignId: string) =>
    `/prints/annotate/${encodeURIComponent(printDesignId)}` as const,
} as const;

/**
 * Workbench landing paths registered in `app/routes.ts`. Composer authors can
 * still add another path; the generic `/:workbench` route hosts it.
 */
export const workbenchPaths = ["/colours-palettes", "/materials", "/partners", "/styles"] as const;

/** Older composers minted this; never persist it as a new sidebar href. */
export const PLACEHOLDER_WORKBENCH_PATH = "/new";

/** Empty or the legacy `/new` placeholder — not a route the author chose. */
export const isPlaceholderWorkbenchPath = (path: string | undefined): boolean => {
  const trimmed = path?.trim();
  return !trimmed || trimmed === PLACEHOLDER_WORKBENCH_PATH || trimmed === "new";
};
